import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function deepEquals(
  obj1: any,
  obj2: any,
  seen = new WeakMap(),
): boolean {
  // Check if both references are identical
  if (obj1 === obj2) return true;

  // Check for null or undefined
  if (obj1 == null || obj2 == null) return false;

  // Check if types differ
  if (typeof obj1 !== typeof obj2) return false;

  // Handle non-object types (string, number, boolean, function, etc.)
  if (typeof obj1 !== "object") return obj1 === obj2;

  // Handle Dates
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Handle Arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEquals(obj1[i], obj2[i], seen)) return false;
    }
    return true;
  }

  // Handle Sets
  if (obj1 instanceof Set && obj2 instanceof Set) {
    if (obj1.size !== obj2.size) return false;
    return deepEquals(Array.from(obj1), Array.from(obj2), seen);
  }

  // Handle Maps
  if (obj1 instanceof Map && obj2 instanceof Map) {
    if (obj1.size !== obj2.size) return false;
    for (const [key, value] of obj1) {
      if (!obj2.has(key) || !deepEquals(value, obj2.get(key), seen))
        return false;
    }
    return true;
  }

  // Prevent circular reference issues
  if (seen.has(obj1)) return seen.get(obj1) === obj2;
  seen.set(obj1, obj2);

  // Handle Objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (
      !Object.prototype.hasOwnProperty.call(obj2, key) ||
      !deepEquals(obj1[key], obj2[key], seen)
    ) {
      return false;
    }
  }

  return true;
}

export const formatLargeNumber = (
  num: number | null | undefined,
  { decimals = 2, prefix = "$" }: { decimals: number; prefix?: string } = {
    decimals: 2,
    prefix: "$",
  },
) => {
  if (num == null || isNaN(num)) return "N/A";
  if (num >= 1e9) return `${prefix}${(num / 1e9).toFixed(decimals)}B`;
  if (num >= 1e6) return `${prefix}${(num / 1e6).toFixed(decimals)}M`;
  if (num >= 1e3) return `${prefix}${(num / 1e3).toFixed(decimals)}K`;
  return `${prefix}${num.toFixed(decimals)}`;
};

export function safeParseDate(str: string): Date | null {
  if (!str) return null;
  try {
    return new Date(str);
  } catch {
    return null;
  }
}
